import {
  AutoCustomCompleteField,
  CustomDateControl,
  FormShell,
  Input,
  MessageAlert,
  RBButton,
  FormGrid,
  FormField,
} from "@scmlatam/web-ui";
import { useEffect } from "react";
import type {
  AlertProps,
  UpdateFooterProps,
  UpdateEmployeeDialogProps,
} from "../types/component";

const Alert = ({
  t,
  showMessage,
  setShowMessage,
  onClickCancel,
}: AlertProps) => {
  if (showMessage) {
    return (
      <MessageAlert
        type="warning"
        title={t("edit_worker.alerts.unsaved_changes.title")}
        message={t("edit_worker.alerts.unsaved_changes.message")}
        className="rb-message-alert--form"
        actions={[
          {
            label: t("edit_worker.alerts.unsaved_changes.action_button"),
            onClick: () => setShowMessage(false),
          },
          {
            label: t("edit_worker.alerts.unsaved_changes.cancel_button"),
            onClick: () => {
              setShowMessage(false);
              onClickCancel();
            },
          },
        ]}
      />
    );
  }
  return undefined;
};

const Footer = ({ t, onClickCancel, onClickSave }: UpdateFooterProps) => {
  return (
    <div className="flex flex-wrap items-center justify-end gap-3 p-3">
      <RBButton variant="secondary" size="m" onClick={onClickCancel}>
        {t("edit_worker.buttons.cancel")}
      </RBButton>
      <RBButton size="m" onClick={onClickSave}>
        {t("edit_worker.buttons.save_changes")}
      </RBButton>
    </div>
  );
};

const UpdateEmployeeDialog= ({ formProps }: UpdateEmployeeDialogProps) => {
  useEffect(() => {}, [formProps.validationErrors]);

  return (
    <FormShell
      open={formProps.openForm}
      title={formProps.title}
      size="m"
      onClose={() => formProps.setOpenForm(false)}
      loading={formProps.loadingForm}
      alert={
        <Alert
          t={formProps.t}
          showMessage={formProps.showMessage}
          setShowMessage={formProps.setShowMessage}
          onClickCancel={() => formProps.setOpenForm(false)}
        />
      }
      footer={
        <Footer
          t={formProps.t}
          onClickCancel={() => {
            if (formProps.hasChanges()) {
              formProps.setShowMessage(true);
            } else {
              formProps.setOpenForm(false);
            }
          }}
          onClickSave={() => {
            const hasErrors = formProps.checkInvalidData();
            if (!hasErrors) {
              formProps.updateEmployee();
            }
          }}
        />
      }
    >
      <form id="employee-edit-form">
        <FormGrid>
          <FormField span={6}>
            <Input
              type="text"
              label={formProps.t("edit_worker.fields.first_name")}
              labelVariant="notched"
              placeholder={formProps.t("edit_worker.placeholder.first_name")}
              value={formProps.firstName}
              onChange={(event) => formProps.setFirstName(event.target.value)}
              error={formProps.validationErrors.firstName.error}
              helperText={
                formProps.validationErrors.firstName.error
                  ? formProps.validationErrors.firstName.message
                  : ""
              }
              required
            />
          </FormField>

          <FormField span={6}>
            <Input
              type="text"
              label={formProps.t("edit_worker.fields.last_name")}
              labelVariant="notched"
              placeholder={formProps.t("edit_worker.placeholder.last_name")}
              value={formProps.lastName}
              onChange={(event) => formProps.setLastName(event.target.value)}
              error={formProps.validationErrors.lastName.error}
              helperText={
                formProps.validationErrors.lastName.error
                  ? formProps.validationErrors.lastName.message
                  : ""
              }
              required
            />
          </FormField>

          <FormField span={4}>
            <AutoCustomCompleteField
              label={formProps.t("edit_worker.fields.document_type")}
              placeholder={formProps.t("edit_worker.placeholder.document_type")}
              values={formProps.documentTypes}
              value={[formProps.documentType]}
              setValue={(newValue) => {
                formProps.setDocumentType(newValue[newValue.length - 1] ?? "");
              }}
              white
              error={formProps.validationErrors.documentType.error}
              helperText={
                formProps.validationErrors.documentType.error
                  ? formProps.validationErrors.documentType.message
                  : ""
              }
              search={{
                value: formProps.searchDocumentType,
                onChange: formProps.setSearchDocumentType,
                placeholder: formProps.t("edit_worker.placeholder.search"),
                mode: "local",
                emptyText: formProps.t("edit_worker.placeholder.empty"),
              }}
              required
            />
          </FormField>

          <FormField span={8}>
            <Input
              type="text"
              label={formProps.t("edit_worker.fields.document_number")}
              labelVariant="notched"
              placeholder={formProps.t("edit_worker.placeholder.document_number")}
              value={formProps.documentNumber}
              onChange={(event) =>
                formProps.setDocumentNumber(event.target.value)
              }
              error={formProps.validationErrors.documentNumber.error}
              helperText={
                formProps.validationErrors.documentNumber.error
                  ? formProps.validationErrors.documentNumber.message
                  : ""
              }
              required
            />
          </FormField>

          <FormField span={12}>
            <Input
              type="email"
              label={formProps.t("edit_worker.fields.email")}
              labelVariant="notched"
              placeholder={formProps.t("edit_worker.placeholder.email")}
              value={formProps.email}
              onChange={(event) => formProps.setEmail(event.target.value)}
              error={formProps.validationErrors.email.error}
              helperText={
                formProps.validationErrors.email.error
                  ? formProps.validationErrors.email.message
                  : ""
              }
              required
            />
          </FormField>

          <FormField span={12}>
            <Input
              type="text"
              label={formProps.t("edit_worker.fields.address")}
              labelVariant="notched"
              placeholder={formProps.t("edit_worker.placeholder.address")}
              value={formProps.address}
              onChange={(event) => formProps.setAddress(event.target.value)}
              error={formProps.validationErrors.address.error}
              helperText={
                formProps.validationErrors.address.error
                  ? formProps.validationErrors.address.message
                  : ""
              }
              required
            />
          </FormField>

          <FormField span={6}>
            <AutoCustomCompleteField
              label={formProps.t("edit_worker.fields.country_code")}
              placeholder={formProps.t("edit_worker.placeholder.country_code")}
              values={formProps.codes}
              value={[formProps.code]}
              setValue={(newValue) => {
                formProps.setCode(newValue[newValue.length - 1] ?? "");
              }}
              white
              error={formProps.validationErrors.code.error}
              helperText={
                formProps.validationErrors.code.error
                  ? formProps.validationErrors.code.message
                  : ""
              }
              search={{
                value: formProps.searchCode,
                onChange: formProps.setSearchCode,
                placeholder: formProps.t("edit_worker.placeholder.search"),
                mode: "local",
                emptyText: formProps.t("edit_worker.placeholder.empty"),
              }}
              required
            />
          </FormField>

          <FormField span={6}>
            <Input
              type="tel"
              label={formProps.t("edit_worker.fields.phone")}
              labelVariant="notched"
              placeholder={formProps.t("edit_worker.placeholder.phone")}
              value={formProps.phoneNumber}
              onChange={(event) => formProps.setPhoneNumber(event.target.value)}
              error={formProps.validationErrors.phoneNumber.error}
              helperText={
                formProps.validationErrors.phoneNumber.error
                  ? formProps.validationErrors.phoneNumber.message
                  : ""
              }
              required
            />
          </FormField>

          <FormField span={6}>
            <CustomDateControl
              label={formProps.t("edit_worker.fields.birthdate")}
              initialValue={null}
              value={formProps.birthdate}
              setValue={formProps.setBirthdate}
              disabled={false}
              white
              fullWidth
              error={formProps.validationErrors.birthdate.error}
              helperText={
                formProps.validationErrors.birthdate.error
                  ? formProps.validationErrors.birthdate.message
                  : ""
              }
              pickerProps={{ captionLayout: "dropdown" }}
              required
            />
          </FormField>

          <FormField span={6}>
            <AutoCustomCompleteField
              label={formProps.t("edit_worker.fields.timezone")}
              placeholder={formProps.t("edit_worker.placeholder.timezone")}
              values={formProps.timeZones}
              value={[formProps.timeZone]}
              setValue={(newValue) => {
                formProps.setTimeZone(newValue[newValue.length - 1] ?? "");
              }}
              white
              error={formProps.validationErrors.timeZone.error}
              helperText={
                formProps.validationErrors.timeZone.error
                  ? formProps.validationErrors.timeZone.message
                  : ""
              }
              search={{
                value: formProps.searchTimeZone,
                onChange: formProps.setSearchTimeZone,
                placeholder: formProps.t("edit_worker.placeholder.search"),
                mode: "local",
                emptyText: formProps.t("edit_worker.placeholder.empty"),
              }}
              required
            />
          </FormField>
        </FormGrid>
      </form>
    </FormShell>
  );
};

export { UpdateEmployeeDialog};
